import type { AssetCategory, ContentType } from "@/Dashboard/pages/AssetLibrary/types";
import type { MenuItem } from "@/VisualBuilder/components/LeftHandMenu/types.js";
import { EXPERIENCE_PRIMARY_FILTER, TAB_STATUSES, VB_MODE } from "@/constants";
import type { ThunkDispatch } from "@/store/hooks";
import type { Asset, State, Tag, VisualBuilderDataState } from "@/store/types.js";
import { updateVisualBuilderData } from "@/store/v2/visualBuilderData/actions.js";
import {
  type VisualBuilderDataSelectorParams,
  visualBuilderDataSelector,
} from "@/store/v2/visualBuilderData/selectors";
import type { VisualBuilderModeResult } from "@/store/v2/visualBuilderData/types";
import {
  getDrawerOptions,
  getSortedContentTypes,
  getSortedMediaItems,
  getSortedTags,
  getVisualBuilderFilteredContentTypes,
  getVisualBuilderFilteredMediaItems,
  getVisualBuilderFilteredTags,
  getVisualBuilderMode,
  transformContentTypesData,
  transformMediaItemData,
  transformTagsData,
} from "@/utils/visualBuilder.js";

import { sortMenuItems } from "../drawerUtils";

/**
 * Initial state for the visual builder data.
 * Contains default values for filters, drawer states, views, and other UI configurations.
 */
export const visualBuilderDataInitialState: VisualBuilderDataState = {
  // Filter-related defaults
  primaryFilter: EXPERIENCE_PRIMARY_FILTER.TAG,
  typeFilterId: undefined,
  tagFilterId: undefined,

  // Drawer visibility settings - note special handling for shared URLs
  leftDrawer: true,
  leftDrawerAutoHide: window.location.search.includes("shareId="),
  rightDrawer: true,
  rightDrawerAutoHide: false,
  gridView: false,

  // UI state configurations
  tagsToggled: {},
  carousel: false,
  typeTagFilter: false,
  searchField: false,
  currentMediaItemId: undefined,

  // Data containers
  search: {},
  contentTypes: [] as ContentType[],
  filteredContentTypes: [],
  tags: [] as Tag[],
  filteredTags: [],
  mediaItems: [] as Asset[],
  filteredMediaItems: [],

  // Relationship mappings
  contentTypeTags: {},
  tagContentTypes: {},
  validContentTypes: {},
  validTags: {},
  tagMediaItems: {},
  filteredTagMediaItems: {},

  // State flags and ordering
  searchPerformed: false,
  contentsOrdered: {},
  experienceContentTypeIds: {},
  experienceTagIds: {},
  contentTypesOrdered: {},
  tagsOrdered: {},
  hiddenContentTypes: {},
  hiddenTags: {},
  locked: false,
  shouldToggleTagItem: true,

  // Asset-related state
  assetCategories: [] as AssetCategory[],
  assetCategoriesOrdered: {},
  assetCategoriesToggled: {},
  assetCategoryContentTypes: {},
  assetCategoryFilterId: undefined,
  assetCategoryMediaItems: {},
  experienceAssetCategoryIds: {},
  filteredAssetCategories: [],
  filteredAssetCategoryMediaItems: {},
  hiddenAssetCategories: {},
  typesToggled: {},

  assetCategoryNames: {},
  tagNames: {},
  id: undefined,
  experienceAssets: [],
  propertyId: -1,
};

type Payload = typeof visualBuilderDataInitialState & {
  assetCategoryTabData?: {
    mediaItems: Asset[];
    assetCategoryHiddenMediaItems: Asset[];
    contentTypeHiddenMediaItems: Asset[];
  };
  contentTypeTabData?: {
    mediaItems: Asset[];
    assetCategoryHiddenMediaItems: Asset[];
    contentTypeHiddenMediaItems: Asset[];
  };
};

/**
 * Action creator to set initial values for presentation mode
 * @param {Object} params - Configuration parameters including tabStatus
 * @returns {Function} Thunk action function
 */
export function addPresentationInitialValues(
  params: VisualBuilderDataSelectorParams & {
    propertyMenuItems?: MenuItem[];
  },
): (dispatch: ThunkDispatch, getState: () => State) => Promise<void> {
  return async (dispatch: ThunkDispatch, getState: () => State) => {
    const { tabStatus, propertyMenuItems = [] } = params;
    const { mode } = getVisualBuilderMode(params) as VisualBuilderModeResult;
    const { visualBuilderData } = visualBuilderDataSelector({ tabStatus })(getState());

    const { mediaItems = [], contentTypes = [], tags = [], filteredAssetCategories = [] } = visualBuilderData;

    const sortedAssetCategories = sortMenuItems({
      parentId: 0,
      itemType: "asset-category",
      items: filteredAssetCategories,
      menuItems: propertyMenuItems,
    }) as AssetCategory[];

    const initialCategory: AssetCategory | undefined = sortedAssetCategories[0];

    const sortedContentTypes = (sortMenuItems({
      parentId: initialCategory?.id,
      itemType: "content-type",
      items: contentTypes,
      menuItems: propertyMenuItems,
    }) || []) as ContentType[];

    const initialContentTypes = sortedContentTypes.filter((contentType) =>
      contentType.assetCategories?.includes(initialCategory?.id),
    );

    const initialContentType: ContentType | undefined = initialContentTypes[0];

    const sortedMediaItems = sortMenuItems({
      parentId: initialContentType?.id,
      itemType: "media-item",
      items: mediaItems,
      menuItems: propertyMenuItems,
    }) as Asset[];

    const initialMediaItems = sortedMediaItems.filter((mediaItem) => {
      const { contentTypeId = -1 } = mediaItem;

      return contentTypeId === initialContentType.id;
    });

    const initialMediaItem: Asset | undefined = initialMediaItems[0];

    // Only proceed if in presentation mode
    if (mode !== VB_MODE.PRESENTATION) {
      return;
    }

    // Set initial values for homebase and collect tabs if media items exist
    if ((tabStatus === TAB_STATUSES.HOMEBASE || tabStatus === TAB_STATUSES.COLLECT) && mediaItems.length) {
      const values: {
        gridView: boolean;
        typeFilterId?: number;
        tagFilterId?: number;
        assetCategoryFilterId?: number;
        currentMediaItemId?: number;
      } = { gridView: false };

      if (initialContentType) {
        values.typeFilterId = initialContentType.id;
      }

      if (values.typeFilterId) {
        const initialTag = tags.find(
          (tag) => values.typeFilterId && tag.contentTypes?.includes(values.typeFilterId) && !tag.hidden,
        );
        if (initialTag) {
          values.tagFilterId = initialTag.id;
        }
      }

      // Set initial asset category
      if (values.typeFilterId) {
        if (initialCategory) {
          values.assetCategoryFilterId = initialCategory.id;
        }
      }

      // Set initial media item based on selected filters
      if (values.typeFilterId && values.tagFilterId) {
        if (initialMediaItem) {
          values.currentMediaItemId = initialMediaItem.id;
        }
      }

      dispatch(updateVisualBuilderData(params, values, {}, true));
    }
  };
}

/**
 * Action creator to set initial values for visualization mode
 * @param {Object} params - Configuration parameters
 * @returns {Function} Thunk action function
 */
export function addVisualizationInitialValues(
  params: VisualBuilderDataSelectorParams,
): (dispatch: ThunkDispatch, getState: () => State) => Promise<void> {
  return async (dispatch: ThunkDispatch, getState: () => State) => {
    const { tabStatus } = params;
    const { mode } = getVisualBuilderMode(params) as VisualBuilderModeResult;
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    const { filteredMediaItems = [], gridView } = visualBuilderData;

    // Only proceed for visualization mode
    if (mode !== VB_MODE.VISUALIZATION) {
      return;
    }

    // Set initial media item for customize tab
    if (tabStatus === TAB_STATUSES.CUSTOMIZE && filteredMediaItems.length && !gridView) {
      const values: {
        currentMediaItemId?: number;
      } = {};
      const initialMediaItem = filteredMediaItems[0];

      if (initialMediaItem) {
        values.currentMediaItemId = initialMediaItem.id;
      }

      if (Object.keys(values).length) {
        dispatch(updateVisualBuilderData(params, values));
      }
    }
  };
}

/**
 * Action creator to set initial values for public visualization
 * @param {Object} params - Configuration parameters
 * @returns {Function} Thunk action function
 */
export function addPublicVisualizationInitialValues(
  params: VisualBuilderDataSelectorParams,
): (dispatch: ThunkDispatch, getState: () => State) => Promise<void> {
  return async (dispatch: ThunkDispatch, getState: () => State) => {
    const { tabStatus } = params;
    const { mode } = getVisualBuilderMode(params) as VisualBuilderModeResult;
    const { visualBuilderData } = visualBuilderDataSelector(params)(getState());

    const { currentMediaItemId, filteredMediaItems = [], gridView, mediaItems = [], scope } = visualBuilderData;

    // Check if we're in a public visualization mode
    if (
      !(
        mode === VB_MODE.PUBLIC_VISUALIZATION ||
        mode === VB_MODE.PUBLIC_MEDIA_SHARE_VISUALIZATION ||
        mode === VB_MODE.PUBLIC_SINGLE_SHARE_VISUALIZATION
      )
    ) {
      return;
    }

    // Set initial drawer options and values
    const values = {
      ...(getDrawerOptions({ tabStatus, mediaItems, scope }) as {
        leftDrawerAutoHide: boolean;
        rightDrawerAutoHide: boolean;
        leftDrawer?: boolean;
      }),
    };

    let newCurrentMediaItemId = currentMediaItemId;

    // Set initial media item for public tab
    if (tabStatus === TAB_STATUSES.PUBLIC && filteredMediaItems.length && !gridView && !currentMediaItemId) {
      const initialMediaItem = filteredMediaItems[0];

      if (initialMediaItem) {
        newCurrentMediaItemId = initialMediaItem.id;
      }
    }

    if (Object.keys(values).length) {
      dispatch(updateVisualBuilderData(params, { ...values, currentMediaItemId: newCurrentMediaItemId }));
    }
  };
}

export function buildTransformedPayload(payload: Payload) {
  const transformedContentTypesPayload = {
    ...payload,
    ...transformContentTypesData({
      contentTypes: payload.contentTypes,
      contentTypesOrdered: payload.contentTypesOrdered,
      propertyId: payload.propertyId,
    }),
  };

  const transformedTagsPayload = {
    ...transformedContentTypesPayload,
    ...transformTagsData({
      hiddenTags: transformedContentTypesPayload.hiddenTags,
      assetCategoriesOrdered: transformedContentTypesPayload.assetCategoriesOrdered,
      assetCategories: transformedContentTypesPayload.assetCategories,
      contentTypes: transformedContentTypesPayload.contentTypes,
      tags: transformedContentTypesPayload.tags,
      tagsOrdered: transformedContentTypesPayload.tagsOrdered,
    }),
  };

  return {
    ...transformedTagsPayload,
    ...transformMediaItemData({
      mediaItems: transformedTagsPayload.mediaItems,
      contentTypes: transformedTagsPayload.contentTypes,
      tags: transformedTagsPayload.tags,
      contentTypeTags: transformedTagsPayload.contentTypeTags,
      tagContentTypes: transformedTagsPayload.tagContentTypes,
      assetCategories: transformedTagsPayload.assetCategories,
      assetCategoryContentTypes: transformedTagsPayload.assetCategoryContentTypes,
      contentTypeAssetCategories: transformedTagsPayload.contentTypeAssetCategories,
      hiddenTags: transformedTagsPayload.hiddenTags,
      otherTag: transformedTagsPayload.otherTag,
      tabStatus: transformedTagsPayload.tabStatus,
      hiddenContentTypes: transformedTagsPayload.hiddenContentTypes,
      hiddenAssetCategories: transformedTagsPayload.hiddenAssetCategories,
      propertyId: transformedTagsPayload.propertyId,
      primaryFilter: transformedTagsPayload.primaryFilter,
    }),
  };
}

export function buildSortedPayload(payload: Payload) {
  const sortedContentTypePayload = {
    ...payload,
    ...getSortedContentTypes({
      contentTypes: payload.contentTypes,
      contentTypesOrdered: payload.contentTypesOrdered,
    }),
  };

  const sortedTagPayload = {
    ...sortedContentTypePayload,
    ...getSortedTags({
      tags: sortedContentTypePayload.tags,
      tagsOrdered: sortedContentTypePayload.tagsOrdered,
      assetCategories: sortedContentTypePayload.assetCategories,
      assetCategoriesOrdered: sortedContentTypePayload.assetCategoriesOrdered,
    }),
  };

  return {
    ...sortedTagPayload,
    ...getSortedMediaItems({
      typeFilterId: sortedTagPayload.typeFilterId,
      assetCategoryFilterId: sortedTagPayload.assetCategoryFilterId,
      mediaItems: sortedTagPayload.mediaItems,
      experienceAssets: sortedTagPayload.experienceAssets,
    }),
  };
}

export function buildFilteredPayload(
  payload: Payload & {
    otherTag?: Tag;
    propertyMenuItems?: MenuItem[];
    tabStatus?: string;
    contentTypeAssetCategories?: Record<string, number[]>;
    assetCategoryTabData?: {
      mediaItems: Asset[];
      assetCategoryHiddenMediaItems: Asset[];
      contentTypeHiddenMediaItems: Asset[];
    };
    contentTypeTabData?: {
      mediaItems: Asset[];
      assetCategoryHiddenMediaItems: Asset[];
      contentTypeHiddenMediaItems: Asset[];
    };
  },
) {
  const filteredMediaItemsPayload = {
    ...payload,
    ...getVisualBuilderFilteredMediaItems({
      mediaItems: payload.mediaItems,
      typeFilterId: payload.typeFilterId,
      assetCategoryFilterId: payload.assetCategoryFilterId,
      search: payload.search,
      otherTag: payload.otherTag,
      primaryFilter: payload.primaryFilter,
      propertyMenuItems: payload.propertyMenuItems,
      currentMediaItemId: payload.currentMediaItemId,
      assetCategoryHiddenMediaItems: payload.assetCategoryHiddenMediaItems,
      contentTypeHiddenMediaItems: payload.contentTypeHiddenMediaItems,
    }),
  };

  const filteredContentTypesPayload = {
    ...filteredMediaItemsPayload,
    ...getVisualBuilderFilteredContentTypes({
      validContentTypes: filteredMediaItemsPayload.validContentTypes,
      searchPerformed: filteredMediaItemsPayload.searchPerformed,
      hiddenContentTypes: filteredMediaItemsPayload.hiddenContentTypes,
      contentTypesOrdered: filteredMediaItemsPayload.contentTypesOrdered,
      contentTypes: filteredMediaItemsPayload.contentTypes,
      tabStatus: filteredMediaItemsPayload.tabStatus,
    }),
  };

  return {
    ...filteredContentTypesPayload,
    ...getVisualBuilderFilteredTags({
      primaryFilter: filteredContentTypesPayload.primaryFilter,
      typeFilterId: filteredContentTypesPayload.typeFilterId,
      contentTypeTags: filteredContentTypesPayload.contentTypeTags,
      validTags: filteredContentTypesPayload.validTags,
      hiddenTags: filteredContentTypesPayload.hiddenTags,
      searchPerformed: filteredContentTypesPayload.searchPerformed,
      tagsOrdered: filteredContentTypesPayload.tagsOrdered,
      tagNames: filteredContentTypesPayload.tagNames,
      filteredTagMediaItems: filteredContentTypesPayload.filteredTagMediaItems,
      filteredAssetCategoryMediaItems: filteredContentTypesPayload.filteredAssetCategoryMediaItems,
      assetCategoriesOrdered: filteredContentTypesPayload.assetCategoriesOrdered,
      contentTypeAssetCategories: filteredContentTypesPayload.contentTypeAssetCategories,
      assetCategoryNames: filteredContentTypesPayload.assetCategoryNames,
      tabStatus: filteredContentTypesPayload.tabStatus,
      tags: filteredContentTypesPayload.tags,
      assetCategories: filteredContentTypesPayload.assetCategories,
      currentMediaItemId: filteredContentTypesPayload.currentMediaItemId,
      shouldToggleTagItem: filteredContentTypesPayload.shouldToggleTagItem,
      mediaItems: filteredContentTypesPayload.mediaItems,
      tagsToggled: filteredContentTypesPayload.tagsToggled,
    }),
  };
}
